import {inject, Injectable} from '@angular/core';
import {BannerService} from '../banner/banner.service';
import {errorText, onceWhileShown} from '../graphql/error-link';
import {CombinedGraphQLErrors} from '@apollo/client/errors';
import {RepositoryProcessPayload} from '../types-repository';
import {WorkflowProcessPayload} from '../types-workflow';
import {Process as SigniantProcess} from '../types-signiant';
import {BehaviorSubject, Observable, timer} from 'rxjs';
import {Apollo, ApolloBase} from 'apollo-angular';
import gql from 'graphql-tag';
import {filter, map} from 'rxjs/operators';
import {environment} from '../../environments/environment';


/**
 * Every API models a long-running action the same way -- id/state/message/data/
 * action/start_date/end_date -- so one poller serves them all. What differs per
 * API is which named Apollo client to ask and how the single-process query is
 * spelled: repository and workflow expose `process(id:)`, signiant wraps its
 * arguments in an input object (`process(input: {id:})`).
 */
const PROCESS_SOURCES: {
  [typename: string]: { client?: string; field: (id: string) => string }
} = {
  RepositoryProcessPayload: {field: (id) => `process(id: "${id}")`},
  WorkflowProcessPayload: {client: 'workflow', field: (id) => `process(id: "${id}")`},
  Process: {client: 'signiant', field: (id) => `process(input: {id: "${id}"})`},
};

export type AnyProcess = WorkflowProcessPayload | RepositoryProcessPayload | SigniantProcess;

@Injectable({
  providedIn: 'root'
})
export class ProcessService {
  processObservers: { [key: string]: [BehaviorSubject<AnyProcess>, string, string, string] } = {};

  /** One banner per distinct status-check failure while it is shown; the poll keeps running. */
  private readonly banner = inject(BannerService);
  private readonly reportFailure = onceWhileShown(message => this.banner.open(message, ['Dismiss']));

  constructor(private apollo: Apollo) {
    Object.keys(PROCESS_SOURCES).forEach(typename => this.startMonitoring(typename));
  }

  private startMonitoring(processType: string) {
    timer(0, 3000).pipe(
      map(() => this.getInProgressObservers(processType)),
      filter(observers => Object.keys(observers).length > 0)
    ).subscribe((procs) => this.executeQuery(procs));
  }

  private getInProgressObservers(processType: string): {
    [key: string]: [BehaviorSubject<AnyProcess>, string, string, string]
  } {
    return Object.entries(this.processObservers)
      .filter(([_, value]) =>
        value[0].value.state === 'IN_PROGRESS' &&
        value[0].value.__typename === processType
      )
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {} as { [key: string]: [BehaviorSubject<AnyProcess>, string, string, string] });
  }

  private executeQuery(procs: {
    [key: string]: [BehaviorSubject<AnyProcess>, string, string, string]
  }) {
    const timestamp = Date.now().toString();
    const typename = Object.values(procs)[0][0].value.__typename;
    const source = PROCESS_SOURCES[typename];
    let queryText = `query GetProcess_${timestamp} {\n`;

    for (const processId of Object.keys(procs)) {
      queryText += this.getQueryFragment(processId, source);
    }
    queryText += `}`;
    const myApollo: ApolloBase = source?.client ? this.apollo.use(source.client) : this.apollo;
    myApollo.query({
      query: gql(queryText),
      fetchPolicy: 'network-only'
    }).subscribe({
      next: res => this.handleResponse(res.data),
      error: err => {
        console.error(err);
        // A network failure is already reported by the app-wide link; a refused query is not.
        if (CombinedGraphQLErrors.is(err)) {
          this.reportFailure(`Could not check the status of running processes: ${errorText(err)}`);
        }
      }
    });
  }

  private getQueryFragment(processId: string, source: { field: (id: string) => string }) {
    const formattedId = processId.replace(/-/g, '_');
    return `  process_${formattedId}: ${source.field(processId)} {\n    action\n    data\n    id\n    message\n    state\n    start_date\n    end_date\n  }\n`;
  }

  private handleResponse(data: any) {
    Object.keys(data).forEach(key => {
      const process = data[key];
      if (!process) {
        return;
      }

      const observer = this.processObservers[process.id][0];
      const currentProcess = observer.value;

      if (process.state === 'FAILED' || process.state === 'SUCCESS') {
        console.log(`process ${process.id} ${process.state}`, this.consoleUrl(process));
        observer.next(process);
        observer.complete();
        const notifyTarget = process.state === 'FAILED' ? 2 : 1;
        if (this.processObservers[process.id][notifyTarget]) {
          this.notify(this.processObservers[process.id][notifyTarget],
            process.state === 'FAILED' ? 'circle-exclamation' : 'circle-check',
            this.processObservers[process.id][3]
          );
        }
      } else if (JSON.stringify(currentProcess) !== JSON.stringify(process)) {
        observer.next(process);
      }
    });
  }

  public observeProcess<T extends AnyProcess>(
    process: T,
    options?: { successMessage?: string; failureMessage?: string; link?: string }): Observable<T> {
    console.log('observeProcess', process.id, this.consoleUrl(process));
    if (this.processObservers[process.id] === undefined) {
      this.processObservers[process.id] = [
        new BehaviorSubject<T>(process),
        options?.successMessage,
        options?.failureMessage,
        options?.link];
    } else if (options) {
      // Already watched (same process adopted twice, or two buttons bound to
      // it): keep the single subject and only refresh the notification texts.
      // Guarding on `options` matters -- bind() calls this without any.
      this.processObservers[process.id][1] = options.successMessage;
      this.processObservers[process.id][2] = options.failureMessage;
      this.processObservers[process.id][3] = options.link;
    }
    return this.processObservers[process.id][0] as BehaviorSubject<T>;
  }

  /**
   * Builds a clickable AWS Step Functions console URL for a process execution, so the
   * browser-console logs link straight to the orchestrator execution in the AWS console.
   * The execution name equals process.id; the state machine is the per-env "API-Processes"
   * orchestrator (workflow vs repository depending on the payload type).
   */
  private consoleUrl(process: AnyProcess): string {
    const region = environment.region;
    // signiant runs its own import state machine rather than a shared
    // "API-Processes" orchestrator, so its execution ARN is shaped differently.
    if (process.__typename === 'Process') {
      const arn = `arn:aws:states:${region}:${environment.accountId}:execution:signiant-import-${environment.env_name}:${process.id}`;
      return `https://${region}.console.aws.amazon.com/states/home?region=${region}#/v2/executions/details/${arn}`;
    }
    const stackPrefix = process.__typename === 'WorkflowProcessPayload' ? 'workflow-api' : 'content-api';
    const stateMachineName = `${stackPrefix}-${environment.env_name}-API-Processes`;
    const executionArn = `arn:aws:states:${region}:${environment.accountId}:execution:${stateMachineName}:${process.id}`;
    return `https://${region}.console.aws.amazon.com/states/home?region=${region}#/v2/executions/details/${executionArn}`;
  }

  private notify(message: string, icon: string, link: string) {
    if (!('Notification' in window)) {
      alert('This browser does not support desktop notification');
      return;
    }

    const createNotification = () => {
      const notification = new Notification(message, {
        icon: `assets/icons/${icon}.png`
      });

      if (link) {
        notification.onclick = (event) => {
          event.preventDefault(); // Prevent focusing the Notification's tab
          const url = link.startsWith('http') ? link : `${window.location.origin}${link}`;
          window.open(url, '_blank');
        };
      }
    };

    if (Notification.permission === 'granted') {
      createNotification();
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          createNotification();
        }
      });
    }
  }
}
